import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import reduce from 'lodash/reduce';
import extend from 'lodash/extend';
import get from 'lodash/get';
import pick from 'lodash/pick';
import * as Spinners from '@Services/Spinners';
import RxSpinnerComponent from '@Components/Results/utils/rx-spinner.component';
import RxStructureService from '@Services/RxStructureService';
import SubstanceDetailsContent from '@Components/Results/structure/substance-details/SubstanceDetailsContent';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import * as ResultsDataManage from '@Services/ResultsDataManage';
import ResultsInterface from '@Services/ResultsInterface';

import './SubstanceDetailsModal.scss';

function getRequiredPropsByDatabaseId(databaseId) {
  let reqKeys;

  switch (databaseId) {
    case '1':
      reqKeys = ['pubchemReferences'];
      break;
    case '5':
    case '15':
    case '16':
      reqKeys = ['supplierData'];
      break;
    default:
      reqKeys = ['facts', 'links'];
      break;
  }

  return reqKeys;
}

function SubstanceDetailsModal({ structure, metadata, reactionId, closeMenu, isOpen, loadDetailsOnMount }) {
  const detailsId = get(structure, 'details.id');
  const spinnerIdRef = useRef(`rx-structure-${detailsId}-details-${new Date().valueOf()}`);
  const firstResponseRef = useRef(null);
  const pendingRequestRef = useRef(null);

  const reqKeys = useMemo(() => getRequiredPropsByDatabaseId(get(structure, 'databaseId')), [structure]);

  const [isAllDetailsLoaded, setIsAllDetailsLoaded] = useState(
    !isEmpty(get(structure, 'details')) && reduce(reqKeys, (memo, key) => memo && !isEmpty(get(structure, key)), true)
  );

  const loadSubstanceDetails = async () => {
    firstResponseRef.current = await ResultsDataManage.getSubstancesById({
      structureId: detailsId,
      databaseId: get(structure, 'databaseId'),
    });

    // Render what we have
    if (!firstResponseRef.current?.resultSize) return;

    // Get results
    await new Promise((resolve, reject) => {
      const response = firstResponseRef.current;
      pendingRequestRef.current = ResultsInterface.getResults(
        {
          resultName: response.resultName,
          databaseId: response.databaseId,
          context: response.context,
          hitSetKey: response.hitSetKey,
          firstItem: 1,
          lastItem: 1,
        },
        true
      )?.subscribe({
        next: ({ data: items }) => {
          const substance = items[0] || {};
          extend(structure, pick(substance, ...reqKeys));
          pendingRequestRef.current = null;
          resolve();
        },
        error: reject,
      });
    });
  };

  const onToggle = (shouldOpen) => {
    if (shouldOpen) {
      if (!isAllDetailsLoaded) {
        // Loading details
        Spinners.activate(spinnerIdRef.current);

        loadSubstanceDetails()
          .then(() => {
            setIsAllDetailsLoaded(true);
          })
          .finally(() => {
            Spinners.terminate(spinnerIdRef.current);
          })
          .catch(Logger.error);
      }
    } else if (pendingRequestRef.current) {
      // Cancel pending request
      pendingRequestRef.current.cancel();
      pendingRequestRef.current = null;
    }
  };

  const viewDetailsHandler = () => {
    RxStructureService.viewDetails(reactionId, detailsId, get(structure, 'databaseId'), firstResponseRef.current);
    closeMenu();
  };

  // ComponentDidMount
  useEffect(() => {
    if (loadDetailsOnMount) {
      onToggle(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ComponentDidUpdate for isOpen changes
  useEffect(() => {
    onToggle(isOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // ComponentWillUnmount - cleanup function
  useEffect(
    () => () => {
      // Cancel pending request
      if (pendingRequestRef.current?.cancel) {
        pendingRequestRef.current.cancel();
        pendingRequestRef.current = null;
      }
    },
    []
  );

  return (
    <div className="substance-view-details__content" data-disable-autoclose>
      {/* Loading spinner */}
      <RxSpinnerComponent spinnerId={spinnerIdRef.current} spinnerClass="substance-view-details__spinner" />
      {/* Details content, remount on open */}
      {isOpen && isAllDetailsLoaded && (
        <SubstanceDetailsContent
          structure={structure}
          metadata={metadata}
          isAllDetailsLoaded={isAllDetailsLoaded}
          onViewDetails={viewDetailsHandler}
        />
      )}
    </div>
  );
}

SubstanceDetailsModal.defaultProps = {
  isOpen: false,
  loadDetailsOnMount: false,
};

SubstanceDetailsModal.propTypes = {
  structure: PropTypes.object.isRequired,
  reactionId: PropTypes.string,
  closeMenu: PropTypes.func.isRequired,
  isOpen: PropTypes.bool,
  loadDetailsOnMount: PropTypes.bool,
  metadata: MetadataPropTypesShape,
};

export default SubstanceDetailsModal;
